if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.form.addEventListener('click', (event) => {
          const button = event.target.closest('[type="submit"]');
          if (button && this.form.contains(button)) this.lastSubmitter = button;
        });
        this.submitButton = this.querySelector('[type="submit"][name="add"]') || this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton?.querySelector('span');
        this.buyNowButton = this.querySelector('[data-buy-now]');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.boundClearSizeError = this.clearSizeValidationError.bind(this);
        document.addEventListener('change', this.boundClearSizeError);
        this.ensureErrorMessageWrapper();

        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (document.querySelector('cart-drawer') && this.submitButton) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }
      }

      disconnectedCallback() {
        document.removeEventListener('change', this.boundClearSizeError);
        this.errorWrapperObserver?.disconnect();
      }

      connectedCallback() {
        this.ensureErrorMessageWrapper();
        this.setupErrorWrapperObserver();
      }

      ensureErrorMessageWrapper() {
        if (this.hideErrors) return null;

        const submitButtons = this.querySelectorAll('[type="submit"]');
        const submitButton = submitButtons[submitButtons.length - 1];
        if (!submitButton) return null;

        let wrapper = this.querySelector('.product-form__error-message-wrapper');
        if (!wrapper) {
          wrapper = document.createElement('div');
          wrapper.className =
            'product-form__error-message-wrapper product-form__error-message-wrapper--below-submit';
          wrapper.setAttribute('role', 'alert');
          wrapper.hidden = true;

          const svgWrapper = document.createElement('span');
          svgWrapper.className = 'svg-wrapper';
          svgWrapper.innerHTML =
            '<svg class="icon icon-error" viewBox="0 0 13 13"><circle cx="6.5" cy="6.5" r="5.5" stroke="#fff" stroke-width="2"/><circle cx="6.5" cy="6.5" r="5.5" fill="#EB001B" stroke="#EB001B" stroke-width=".7"/><path fill="#fff" d="m5.874 3.528.1 4.044h1.053l.1-4.044zm.627 6.133c.38 0 .68-.288.68-.656s-.3-.656-.68-.656-.681.288-.681.656.3.656.68.656"/><path fill="#fff" stroke="#EB001B" stroke-width=".7" d="M5.874 3.178h-.359l.01.359.1 4.044.008.341h1.736l.008-.341.1-4.044.01-.359H5.873Zm.627 6.833c.56 0 1.03-.432 1.03-1.006s-.47-1.006-1.03-1.006-1.031.432-1.031 1.006.47 1.006 1.03 1.006Z"/></svg>';

          const messageSpan = document.createElement('span');
          messageSpan.className = 'product-form__error-message';

          wrapper.appendChild(svgWrapper);
          wrapper.appendChild(messageSpan);
        }

        if (wrapper.previousElementSibling !== submitButton) {
          submitButton.insertAdjacentElement('afterend', wrapper);
        }

        this.errorMessageWrapper = wrapper;
        this.errorMessage = wrapper.querySelector('.product-form__error-message');
        return wrapper;
      }

      setupErrorWrapperObserver() {
        const buttons = this.querySelector('.product-form__buttons');
        if (!buttons || this.errorWrapperObserver) return;

        this.errorWrapperObserver = new MutationObserver(() => {
          this.ensureErrorMessageWrapper();
        });
        this.errorWrapperObserver.observe(buttons, { childList: true });
      }

      getVariantSelects() {
        const productInfo = this.closest('product-info');
        return productInfo?.querySelector('variant-selects') || document.querySelector('variant-selects');
      }

      clearSizeValidationError(event) {
        if (!event.target.closest('[data-size-option]')) return;
        this.handleErrorMessage();
        event.target.closest('[data-size-option]')?.classList.remove('product-form__input--error');
      }

      validateSizeSelection() {
        const variantSelects = this.getVariantSelects();
        if (!variantSelects?.hasSizeOption()) return true;

        if (variantSelects.isSizeSelected()) return true;

        const sizeContainer = variantSelects.sizeOptionContainer;
        sizeContainer?.classList.add('product-form__input--error');
        sizeContainer?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        this.handleErrorMessage(window.variantStrings.sizeMandatory || 'size is mandatory');
        return false;
      }

      getVariantIdInput() {
        return this.form?.querySelector('input[name="id"]');
      }

      readVariantIdFromPicker() {
        const variantSelects = this.getVariantSelects();
        const raw = variantSelects?.querySelector('[data-selected-variant]')?.textContent?.trim();
        if (!raw || raw === 'null') return '';

        try {
          const variant = JSON.parse(raw);
          return variant?.id ? String(variant.id) : '';
        } catch (e) {
          return '';
        }
      }

      getSelectedOptionValuesForMatch() {
        const variantSelects = this.getVariantSelects();
        if (!variantSelects) return [];

        return Array.from(
          variantSelects.querySelectorAll('fieldset input[type="radio"]:checked, select option[selected]')
        )
          .filter((el) => el.dataset.optionValueId || el.value)
          .map((el) => ({
            optionValueId: el.dataset.optionValueId || '',
            value: (el.value || '').trim().toLowerCase(),
          }));
      }

      async resolveVariantIdFromProductJson() {
        const productInfo = this.closest('product-info');
        const productUrl = productInfo?.dataset?.url;
        if (!productUrl) return '';

        const selected = this.getSelectedOptionValuesForMatch();
        if (!selected.length) return '';

        try {
          if (!productInfo._everteeProductJson) {
            if (!productInfo._everteeProductJsonPromise) {
              productInfo._everteeProductJsonPromise = fetch(`${productUrl}.js`).then((response) => {
                if (!response.ok) throw new Error('product json');
                return response.json();
              });
            }
            productInfo._everteeProductJson = await productInfo._everteeProductJsonPromise;
          }
          const variants = productInfo._everteeProductJson?.variants || [];
          if (!variants.length) return '';

          const selectedValues = selected.map((s) => s.value).filter(Boolean);
          const match = variants.find((variant) => {
            const options = [variant.option1, variant.option2, variant.option3]
              .filter(Boolean)
              .map((v) => String(v).trim().toLowerCase());
            return selectedValues.every((value) => options.includes(value));
          });

          return match?.id ? String(match.id) : '';
        } catch (e) {
          console.error(e);
          if (productInfo) productInfo._everteeProductJsonPromise = null;
          return '';
        }
      }

      async ensureVariantId() {
        const idInput = this.getVariantIdInput();
        if (!idInput) return false;

        const productInfo = this.closest('product-info');
        const fetchPending = Boolean(productInfo?.pendingRequestUrl);

        if (idInput.value && !fetchPending) return true;

        let variantId = fetchPending ? '' : this.readVariantIdFromPicker();
        if (!variantId) {
          variantId = await this.resolveVariantIdFromProductJson();
        }
        if (!variantId) variantId = this.readVariantIdFromPicker();
        if (!variantId && idInput.value) return true;
        if (!variantId) return false;

        idInput.value = variantId;
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      }

      getActionButtons() {
        return this.querySelectorAll('[type="submit"]');
      }

      getBuyNowButton() {
        return this.querySelector('[data-buy-now]');
      }

      isBuyNowSubmit(evt) {
        const submitter = evt.submitter || this.lastSubmitter || this.form.querySelector('[type="submit"]:focus');
        return Boolean(submitter?.hasAttribute('data-buy-now') || submitter?.name === 'buy_now');
      }

      checkoutUrl() {
        return (
          window.everteeCartRedirect?.checkoutUrl ||
          window.routes?.checkout_url ||
          '/checkout'
        );
      }

      setButtonsBusy(busy, activeButton) {
        this.getActionButtons().forEach((button) => {
          if (busy) {
            button.setAttribute('aria-disabled', 'true');
          } else {
            button.removeAttribute('aria-disabled');
          }
        });

        const spinnerHost = activeButton || this.submitButton;
        if (!spinnerHost) return;

        spinnerHost.classList.toggle('loading', busy);
        spinnerHost.querySelector('.loading__spinner')?.classList.toggle('hidden', !busy);
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        const activeButton = evt.submitter || this.lastSubmitter || this.submitButton;
        if (activeButton?.getAttribute('aria-disabled') === 'true') return;

        const buyNow = this.isBuyNowSubmit(evt);
        window.everteeBuyNowInFlight = buyNow;
        let navigatingAway = false;

        this.handleErrorMessage();

        if (!this.validateSizeSelection()) {
          window.everteeBuyNowInFlight = false;
          return;
        }

        this.setButtonsBusy(true, activeButton);

        const hasVariant = await this.ensureVariantId();
        if (!hasVariant) {
          this.handleErrorMessage(window.variantStrings?.unavailable || 'Cannot find variant');
          this.setButtonsBusy(false, activeButton);
          window.everteeBuyNowInFlight = false;
          return;
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        const redirectAfterAdd =
          buyNow ||
          (typeof window.everteeShouldRedirectAfterAdd === 'function' &&
            window.everteeShouldRedirectAfterAdd());
        if (this.cart && !redirectAfterAdd) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              window.everteeBuyNowInFlight = false;
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (buyNow) {
              navigatingAway = true;
              window.location = this.checkoutUrl();
              return;
            } else if (!this.cart || (window.everteeShouldRedirectAfterAdd && window.everteeShouldRedirectAfterAdd())) {
              navigatingAway = true;
              window.location = window.everteeCartRedirectUrl || window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            window.everteeBuyNowInFlight = false;
            console.error(e);
          })
          .finally(() => {
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!navigatingAway) this.setButtonsBusy(false, activeButton);

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.ensureErrorMessageWrapper();
        if (!this.errorMessageWrapper) return;

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        this.getActionButtons().forEach((button) => {
          if (disable) {
            button.setAttribute('disabled', 'disabled');
          } else {
            button.removeAttribute('disabled');
          }
        });

        const buyNow = this.getBuyNowButton();
        if (buyNow) {
          const hideBuyNow = Boolean(disable && text);
          buyNow.hidden = hideBuyNow;
          buyNow.classList.toggle('hidden', hideBuyNow);
        }

        if (disable) {
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
